import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  alpha,
} from '@mui/material';
import api from '../../services/api';
import SearchableSelect from './SearchableSelect';
import {
  bulkDialogFieldSx,
  bulkDialogFieldDisabledSx,
  bulkDialogPaperSx,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  ACCENT,
} from './SaaSTokens';

const UNSET = '__unset__';

const isFieldActive = (value) => value !== UNSET;

const normalizeOptions = (res) => {
  const data = res?.data;
  return Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
};

const defaultMapOption = (row) => ({
  value: String(row.id),
  label: row.name || row.title || row.tier_name || `#${row.id}`,
});

const dedupeOptionsByValue = (options) => {
  const seen = new Set();
  return options.filter((opt) => {
    const value = String(opt?.value ?? '').trim();
    if (!value || seen.has(value)) return false;
    seen.add(value);
    return true;
  });
};

const fieldWrapSx = (active) => ({
  pl: 1.5,
  ml: -0.5,
  pt: 0.5,
  overflow: 'visible',
  borderLeft: '3px solid',
  borderColor: active ? alpha(ACCENT, 0.55) : 'transparent',
  transition: 'border-color 0.15s ease',
});

const buildSelectOptions = (field, selectOptions, values) => {
  const showNoChange = typeof field.showNoChange === 'function'
    ? field.showNoChange(values)
    : field.showNoChange !== false;

  return [
    ...(showNoChange ? [{ value: UNSET, label: 'No change' }] : []),
    ...(field.clearable ? [{ value: '', label: 'None (clear)' }] : []),
    ...selectOptions,
  ];
};

const isSelectField = (field) => field.type === 'select' || field.type === 'searchableSelect';

const isFieldDisabled = (field, values) => {
  if (typeof field.isDisabled === 'function') {
    return field.isDisabled(values);
  }
  return Boolean(field.disabled);
};

const isFieldRequired = (field, values) => {
  if (typeof field.isRequired === 'function') {
    return field.isRequired(values);
  }
  return Boolean(field.required);
};

const getFieldHint = (field, values, disabled, active) => {
  if (disabled && field.disabledHint) {
    return field.disabledHint;
  }

  if (isFieldRequired(field, values)) {
    const requiredHint = typeof field.requiredHint === 'function'
      ? field.requiredHint(values)
      : field.requiredHint;
    if (requiredHint && !active) {
      return requiredHint;
    }
  }

  if (field.name === 'received_date' && values.status === 'collected' && active) {
    return 'Required when status is Collected.';
  }

  return null;
};

const BulkUpdateDialog = ({
  open,
  onClose,
  selectedIds = [],
  config,
  onSuccess,
  onError,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [optionMap, setOptionMap] = useState({});
  const [values, setValues] = useState({});

  const fields = useMemo(() => {
    if (!config) return [];
    const resolver = config.fields;
    return typeof resolver === 'function' ? resolver(config.user) : (resolver || []);
  }, [config]);

  const changedCount = useMemo(
    () => fields.filter((f) => !isFieldDisabled(f, values) && isFieldActive(values[f.name])).length,
    [fields, values],
  );

  const prevOpenRef = useRef(false);

  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (!justOpened || !config) return;

    setError(null);
    const initial = {};
    fields.forEach((f) => { initial[f.name] = UNSET; });
    setValues(initial);
    setOptionMap({});

    fields.forEach(async (field) => {
      if (!isSelectField(field) || field.options || !field.loadOptions) return;
      try {
        const res = await api.get(field.loadOptions);
        const mapRow = field.mapOption || defaultMapOption;
        let opts = normalizeOptions(res)
          .map((row) => ({ ...mapRow(row), raw: row }))
          .filter((o) => String(o?.value ?? '').trim() !== '');
        if (field.dedupeByValue) {
          opts = dedupeOptionsByValue(opts).sort((a, b) => a.label.localeCompare(b.label));
        }
        setOptionMap((prev) => ({
          ...prev,
          [field.name]: opts,
        }));
      } catch {
        setOptionMap((prev) => ({ ...prev, [field.name]: [] }));
      }
    });
  }, [open, config, fields]);

  const handleChange = (name) => (e) => {
    setValues((prev) => ({ ...prev, [name]: e.target.value }));
  };

  const buildPayload = () => {
    const payload = {};
    fields.forEach((field) => {
      if (isFieldDisabled(field, values)) return;
      const raw = values[field.name];
      if (raw === UNSET || raw === undefined) return;
      if (field.type === 'number') {
        payload[field.name] = raw === '' ? null : Number(raw);
        return;
      }
      if (field.name === 'is_claimable' || field.name === 'is_emergency') {
        payload[field.name] = raw === '1';
        return;
      }
      if (field.clearable && raw === '') {
        payload[field.name] = null;
        return;
      }
      if (field.name.endsWith('_id') && raw !== '') {
        payload[field.name] = Number(raw);
        return;
      }
      payload[field.name] = raw;
    });
    return payload;
  };

  const applySelectChange = (field, value) => {
    setValues((prev) => {
      let next = { ...prev, [field.name]: value };
      if (config?.applyFieldChange) {
        next = config.applyFieldChange(field.name, value, next);
      }
      return next;
    });
  };

  const reportError = (message) => {
    if (typeof onError === 'function') {
      onError(message);
      return;
    }
    setError(message);
  };

  const handleSubmit = async () => {
    const updates = buildPayload();
    if (Object.keys(updates).length === 0) {
      reportError('Choose at least one field to update.');
      return;
    }
    const clientErrors = config?.validatePayload?.(updates, values) || {};
    const clientMessages = Object.values(clientErrors).filter(Boolean);
    if (clientMessages.length > 0) {
      reportError(clientMessages[0]);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await api.put(config.endpoint, { ids: selectedIds.map(Number), ...updates });
      onSuccess?.(updates);
      onClose();
    } catch (err) {
      reportError(
        err.response?.data?.message
          || Object.values(err.response?.data?.errors || {}).flat()?.[0]
          || 'Bulk update failed.',
      );
    } finally {
      setLoading(false);
    }
  };

  const count = selectedIds.length;
  const label = config?.entityLabel || 'record';
  const entityHint = config?.hint || config?.dialogHint;

  const renderFieldHint = (field, active, disabled) => {
    const contextual = field.hint || field.helperText;
    const contextualHint = getFieldHint(field, values, disabled, active);
    const hint = contextualHint || (active && contextual ? contextual : null);
    if (!hint) return null;
    const isRequiredHint = Boolean(contextualHint && isFieldRequired(field, values) && !active);
    return (
      <Typography
        component="span"
        sx={{
          display: 'block',
          mt: 0.75,
          fontSize: '0.75rem',
          color: disabled
            ? alpha(TEXT_SECONDARY, 0.65)
            : isRequiredHint
              ? alpha('#F87171', 0.9)
              : contextualHint
                ? alpha(ACCENT, 0.85)
                : alpha(TEXT_SECONDARY, 0.75),
          lineHeight: 1.4,
        }}
      >
        {hint}
      </Typography>
    );
  };

  const renderField = (field) => {
    const fieldValue = values[field.name];
    const disabled = isFieldDisabled(field, values);
    const required = isFieldRequired(field, values);
    const active = !disabled && isFieldActive(fieldValue);
    const selectOptions = (field.options || optionMap[field.name] || [])
      .filter((option) => (typeof field.filterOption === 'function' ? field.filterOption(option, values) : true));
    const fieldSx = disabled
      ? { ...bulkDialogFieldSx, ...bulkDialogFieldDisabledSx }
      : bulkDialogFieldSx;

    const wrap = (control) => (
      <Box key={field.name} sx={fieldWrapSx(active)}>
        {control}
        {renderFieldHint(field, active, disabled)}
      </Box>
    );

    if (isSelectField(field)) {
      return wrap(
        <SearchableSelect
          label={field.label}
          value={fieldValue ?? UNSET}
          onChange={(v) => applySelectChange(field, v)}
          options={buildSelectOptions(field, selectOptions, values)}
          labelAlwaysShrink
          required={required}
          disabled={disabled}
          sx={fieldSx}
        />,
      );
    }

    if (field.type === 'date') {
      const today = new Date().toISOString().slice(0, 10);
      return wrap(
        <TextField
          fullWidth
          type="date"
          label={field.label}
          value={fieldValue === UNSET ? '' : (fieldValue ?? '')}
          onChange={handleChange(field.name)}
          disabled={disabled}
          required={required}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: today }}
          sx={fieldSx}
        />,
      );
    }

    if (field.type === 'textarea') {
      return wrap(
        <TextField
          fullWidth
          multiline
          minRows={2}
          label={field.label}
          value={fieldValue === UNSET ? '' : (fieldValue ?? '')}
          onChange={handleChange(field.name)}
          disabled={disabled}
          sx={fieldSx}
        />,
      );
    }

    return wrap(
      <TextField
        fullWidth
        type={field.type === 'number' ? 'number' : 'text'}
        label={field.label}
        value={fieldValue === UNSET ? '' : (fieldValue ?? '')}
        onChange={handleChange(field.name)}
        disabled={disabled}
        inputProps={{
          min: field.min,
          max: field.max,
        }}
        sx={fieldSx}
      />,
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: bulkDialogPaperSx }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Typography
            component="span"
            sx={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, color: TEXT_PRIMARY, fontSize: '1.25rem' }}
          >
            Bulk update
          </Typography>
          <Chip
            label={`${count} ${label}${count === 1 ? '' : 's'}`}
            size="small"
            sx={{
              bgcolor: alpha(ACCENT, 0.18),
              color: alpha(TEXT_PRIMARY, 0.95),
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 26,
            }}
          />
        </Stack>
        <Typography sx={{ mt: 0.75, fontSize: '0.8rem', color: alpha(TEXT_SECONDARY, 0.88), fontWeight: 400 }}>
          Change only the fields you want to apply. Unchanged fields stay as-is.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5, px: 3, pb: 2, overflow: 'visible' }}>
        {error && typeof onError !== 'function' && (
          <Alert severity="error" sx={{ mb: 2, fontSize: '0.85rem' }}>
            {error}
          </Alert>
        )}
        {entityHint && (
          <Alert
            severity="info"
            sx={{
              mb: 2,
              py: 0.5,
              fontSize: '0.8rem',
              bgcolor: alpha(ACCENT, 0.08),
              color: alpha(TEXT_PRIMARY, 0.9),
              '& .MuiAlert-icon': { color: alpha(ACCENT, 0.85) },
            }}
          >
            {entityHint}
          </Alert>
        )}
        <Stack spacing={2.5} sx={{ overflow: 'visible', pt: 0.5 }}>
          {fields.map((field) => renderField(field))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1, justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: '0.75rem', color: alpha(TEXT_SECONDARY, 0.7), pl: 0.5 }}>
          {changedCount === 0 ? 'No fields selected' : `${changedCount} field${changedCount === 1 ? '' : 's'} will update`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={loading} sx={{ color: alpha(TEXT_SECONDARY, 0.9) }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || changedCount === 0}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{
              bgcolor: alpha('#3b82f6', 0.85),
              color: '#f1f5f9',
              fontWeight: 600,
              '&:hover': { bgcolor: alpha('#3b82f6', 1) },
              '&.Mui-disabled': {
                opacity: 1,
                bgcolor: alpha('#3b82f6', 0.32),
                color: alpha('#e2e8f0', 0.72),
                border: `1px solid ${alpha('#60a5fa', 0.35)}`,
              },
            }}
          >
            Apply
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default BulkUpdateDialog;
